import type { DashboardLayout, DashboardPanelDTO } from '@tempo-io/roadmap-dto';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUpdateDashboardPanelMutation } from '@/features/dashboards/mutations/useUpdateDashboardPanelMutation';
import { DASHBOARD_LAYOUT_DESCRIPTORS, type DashboardLayoutDescriptor } from './layoutDescriptors';

export type GridSizingPanel = Pick<DashboardPanelDTO, 'position' | 'widthFraction' | 'heightPx'>;

export type StripeSizing = {
  primarySize: number;
  cellCrossSizes: number[];
};

export type DashboardGridSizing = {
  stripes: StripeSizing[];
};

export const MIN_PRIMARY_FRACTION = 0.1; // for column-orient stripe widths
export const MIN_CROSS_FRACTION = 0.1; // for row-orient cell widths
export const MAX_CROSS_FRACTION = 0.9;
export const MIN_HEIGHT_PX = 160;
export const DEFAULT_STRIPE_HEIGHT_PX = 480;

const GRID_GAP_PX = 16; // must match `gap-4` in DashboardGrid

const distributeHeights = (
  slots: (number | null)[],
  total: number | null,
  fallbackEach: number,
): number[] => {
  const nullCount = slots.filter((s) => s === null).length;
  if (nullCount === 0) return slots.map((s) => s ?? 0);

  if (total === null || total <= 0) {
    return slots.map((s) => s ?? fallbackEach);
  }

  const gaps = GRID_GAP_PX * Math.max(0, slots.length - 1);
  const numericSum = slots.reduce<number>((a, s) => a + (s ?? 0), 0);
  const remaining = total - gaps - numericSum;
  const perNull = Math.max(MIN_HEIGHT_PX, remaining / nullCount);
  return slots.map((s) => s ?? perNull);
};

// Slots with a null are left to distributeHeights, which already shares the remainder.
const scaleToTarget = (slots: (number | null)[], targetPx: number | null): (number | null)[] => {
  if (targetPx === null || targetPx <= 0) return slots;
  if (slots.some((s) => s === null)) return slots;

  const gaps = GRID_GAP_PX * Math.max(0, slots.length - 1);
  const available = targetPx - gaps;
  const storedTotal = slots.reduce<number>((a, s) => a + (s ?? 0), 0);
  if (available <= 0 || storedTotal <= 0) return slots;

  const factor = available / storedTotal;
  const scaled = slots.map((s) => Math.max(MIN_HEIGHT_PX, Math.round((s ?? 0) * factor)));

  // The clamp and rounding only push slots up, so trim any excess off the tallest slots.
  let excess = scaled.reduce((a, s) => a + s, 0) - available;
  const tallestFirst = scaled
    .map((_, index) => index)
    .sort((a, b) => (scaled[b] ?? 0) - (scaled[a] ?? 0));
  for (const index of tallestFirst) {
    if (excess <= 0) break;
    const take = Math.min(excess, (scaled[index] ?? 0) - MIN_HEIGHT_PX);
    if (take <= 0) continue;
    scaled[index] = (scaled[index] ?? 0) - take;
    excess -= take;
  }

  return scaled;
};

export const buildFromPanels = (
  descriptor: DashboardLayoutDescriptor,
  panels: GridSizingPanel[],
  containerHeightPx: number | null,
  targetHeightPx?: number | null,
): DashboardGridSizing => {
  const byPosition = new Map(panels.map((p) => [p.position, p]));
  const stripeCount = descriptor.stripes.length;

  if (descriptor.orientation === 'row') {
    const rawStripeHeights = descriptor.stripes.map((stripe) => {
      const firstCell = stripe.cells[0];
      const firstPanel = firstCell ? byPosition.get(firstCell.position) : undefined;
      return firstPanel?.heightPx ?? null;
    });
    const stripeHeights = distributeHeights(
      scaleToTarget(rawStripeHeights, targetHeightPx ?? null),
      targetHeightPx ?? containerHeightPx,
      DEFAULT_STRIPE_HEIGHT_PX,
    );

    return {
      stripes: descriptor.stripes.map((stripe, stripeIdx) => {
        const rawFractions = stripe.cells.map(
          (cell) => byPosition.get(cell.position)?.widthFraction ?? null,
        );
        const cellCrossSizes = normalizeFractions(rawFractions, stripe.cells.length);
        return {
          primarySize: stripeHeights[stripeIdx] ?? DEFAULT_STRIPE_HEIGHT_PX,
          cellCrossSizes,
        };
      }),
    };
  }

  const stripePrimaries = descriptor.stripes.map((stripe) => {
    const firstCell = stripe.cells[0];
    const firstPanel = firstCell ? byPosition.get(firstCell.position) : undefined;
    return firstPanel?.widthFraction ?? null;
  });
  const normalizedStripePrimaries = normalizeFractions(stripePrimaries, stripeCount);

  return {
    stripes: descriptor.stripes.map((stripe, stripeIdx) => {
      const primarySize = normalizedStripePrimaries[stripeIdx] ?? 1 / stripeCount;
      const rawCellHeights = stripe.cells.map(
        (cell) => byPosition.get(cell.position)?.heightPx ?? null,
      );
      const cellCrossSizes = distributeHeights(
        scaleToTarget(rawCellHeights, targetHeightPx ?? null),
        targetHeightPx ?? containerHeightPx,
        DEFAULT_STRIPE_HEIGHT_PX,
      );
      return { primarySize, cellCrossSizes };
    }),
  };
};

const normalizeFractions = (raw: (number | null)[], expectedLength: number): number[] => {
  if (raw.every((f): f is number => f !== null)) {
    const total = raw.reduce((a, b) => a + b, 0);
    if (total > 0) return raw.map((f) => f / total);
  }
  return Array.from({ length: expectedLength }, () => 1 / expectedLength);
};

export const useDashboardGridSizing = (
  dashboardId: number,
  layout: DashboardLayout,
  panels: GridSizingPanel[],
  scrollContainer: HTMLElement | null,
  targetHeightPx?: number | null,
) => {
  const descriptor = DASHBOARD_LAYOUT_DESCRIPTORS[layout];

  const [containerHeightPx, setContainerHeightPx] = useState<number | null>(
    () => scrollContainer?.clientHeight ?? null,
  );

  useEffect(() => {
    if (!scrollContainer) {
      setContainerHeightPx(null);
      return;
    }
    setContainerHeightPx(scrollContainer.clientHeight);
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setContainerHeightPx(entry.contentRect.height);
    });
    observer.observe(scrollContainer);
    return () => observer.disconnect();
  }, [scrollContainer]);

  const sizingFingerprint = useMemo(
    () =>
      panels
        .map((p) => `${p.position}:${p.widthFraction ?? ''}:${p.heightPx ?? ''}`)
        .sort()
        .join('|'),
    [panels],
  );

  const [sizing, setSizing] = useState<DashboardGridSizing>(() =>
    buildFromPanels(descriptor, panels, containerHeightPx, targetHeightPx),
  );

  const panelsRef = useRef(panels);
  panelsRef.current = panels;

  // biome-ignore lint/correctness/useExhaustiveDependencies: fingerprint is a trigger, not a value
  useEffect(() => {
    setSizing(buildFromPanels(descriptor, panelsRef.current, containerHeightPx, targetHeightPx));
  }, [descriptor, sizingFingerprint, containerHeightPx, targetHeightPx]);

  const { mutate: updatePanel } = useUpdateDashboardPanelMutation();

  const writeAnchor = useCallback(
    (position: number, patch: { widthFraction?: number | null; heightPx?: number | null }) => {
      updatePanel({ dashboardId, position, data: patch });
    },
    [dashboardId, updatePanel],
  );

  const previewCrossPair = useCallback(
    (stripeIdx: number, cellIdx: number, leftFractionOfPair: number) => {
      const clamped = Math.max(
        MIN_CROSS_FRACTION,
        Math.min(MAX_CROSS_FRACTION, leftFractionOfPair),
      );
      setSizing((prev) => {
        const stripe = prev.stripes[stripeIdx];
        if (!stripe) return prev;
        const sizes = stripe.cellCrossSizes.slice();
        const a = sizes[cellIdx] ?? 0;
        const b = sizes[cellIdx + 1] ?? 0;
        const pairTotal = a + b;
        sizes[cellIdx] = pairTotal * clamped;
        sizes[cellIdx + 1] = pairTotal * (1 - clamped);
        const nextStripes = prev.stripes.slice();
        nextStripes[stripeIdx] = { ...stripe, cellCrossSizes: sizes };
        return { stripes: nextStripes };
      });
    },
    [],
  );

  const commitCrossPair = useCallback(
    (stripeIdx: number, cellIdx: number, leftFractionOfPair: number) => {
      const stripe = descriptor.stripes[stripeIdx];
      if (!stripe) return;
      const clamped = Math.max(
        MIN_CROSS_FRACTION,
        Math.min(MAX_CROSS_FRACTION, leftFractionOfPair),
      );
      setSizing((prev) => {
        const prevStripe = prev.stripes[stripeIdx];
        if (!prevStripe) return prev;
        const sizes = prevStripe.cellCrossSizes.slice();
        const a = sizes[cellIdx] ?? 0;
        const b = sizes[cellIdx + 1] ?? 0;
        const pairTotal = a + b;
        sizes[cellIdx] = pairTotal * clamped;
        sizes[cellIdx + 1] = pairTotal * (1 - clamped);

        if (descriptor.orientation === 'row') {
          stripe.cells.forEach((cell, i) => {
            writeAnchor(cell.position, { widthFraction: sizes[i] });
          });
        } else {
          const cellA = stripe.cells[cellIdx];
          const cellB = stripe.cells[cellIdx + 1];
          if (cellA) writeAnchor(cellA.position, { heightPx: Math.round(sizes[cellIdx] ?? 0) });
          if (cellB) writeAnchor(cellB.position, { heightPx: Math.round(sizes[cellIdx + 1] ?? 0) });
        }

        const nextStripes = prev.stripes.slice();
        nextStripes[stripeIdx] = { ...prevStripe, cellCrossSizes: sizes };
        return { stripes: nextStripes };
      });
    },
    [descriptor, writeAnchor],
  );

  const previewCrossPairPx = useCallback(
    (stripeIdx: number, cellIdx: number, topPx: number, bottomPx: number) => {
      const top = Math.max(MIN_HEIGHT_PX, Math.round(topPx));
      const bottom = Math.max(MIN_HEIGHT_PX, Math.round(bottomPx));
      setSizing((prev) => {
        const stripe = prev.stripes[stripeIdx];
        if (!stripe) return prev;
        const sizes = stripe.cellCrossSizes.slice();
        sizes[cellIdx] = top;
        sizes[cellIdx + 1] = bottom;
        const nextStripes = prev.stripes.slice();
        nextStripes[stripeIdx] = { ...stripe, cellCrossSizes: sizes };
        return { stripes: nextStripes };
      });
    },
    [],
  );

  const commitCrossPairPx = useCallback(
    (stripeIdx: number, cellIdx: number, topPx: number, bottomPx: number) => {
      const stripe = descriptor.stripes[stripeIdx];
      if (!stripe) return;
      const top = Math.max(MIN_HEIGHT_PX, Math.round(topPx));
      const bottom = Math.max(MIN_HEIGHT_PX, Math.round(bottomPx));
      setSizing((prev) => {
        const prevStripe = prev.stripes[stripeIdx];
        if (!prevStripe) return prev;
        const sizes = prevStripe.cellCrossSizes.slice();
        sizes[cellIdx] = top;
        sizes[cellIdx + 1] = bottom;
        const cellA = stripe.cells[cellIdx];
        const cellB = stripe.cells[cellIdx + 1];
        if (cellA) writeAnchor(cellA.position, { heightPx: top });
        if (cellB) writeAnchor(cellB.position, { heightPx: bottom });
        const nextStripes = prev.stripes.slice();
        nextStripes[stripeIdx] = { ...prevStripe, cellCrossSizes: sizes };
        return { stripes: nextStripes };
      });
    },
    [descriptor, writeAnchor],
  );

  const previewStripePair = useCallback(
    (stripeIdx: number, leftPrimary: number, rightPrimary: number) => {
      setSizing((prev) => {
        const a = prev.stripes[stripeIdx];
        const b = prev.stripes[stripeIdx + 1];
        if (!a || !b) return prev;
        const nextStripes = prev.stripes.slice();
        nextStripes[stripeIdx] = { ...a, primarySize: leftPrimary };
        nextStripes[stripeIdx + 1] = { ...b, primarySize: rightPrimary };
        return { stripes: nextStripes };
      });
    },
    [],
  );

  const commitStripePair = useCallback(
    (stripeIdx: number, leftPrimary: number, rightPrimary: number) => {
      const stripeA = descriptor.stripes[stripeIdx];
      const stripeB = descriptor.stripes[stripeIdx + 1];
      if (!stripeA || !stripeB) return;
      setSizing((prev) => {
        const a = prev.stripes[stripeIdx];
        const b = prev.stripes[stripeIdx + 1];
        if (!a || !b) return prev;
        const nextStripes = prev.stripes.slice();
        nextStripes[stripeIdx] = { ...a, primarySize: leftPrimary };
        nextStripes[stripeIdx + 1] = { ...b, primarySize: rightPrimary };

        const firstA = stripeA.cells[0];
        const firstB = stripeB.cells[0];
        if (descriptor.orientation === 'row') {
          if (firstA) writeAnchor(firstA.position, { heightPx: Math.round(leftPrimary) });
          if (firstB) writeAnchor(firstB.position, { heightPx: Math.round(rightPrimary) });
        } else {
          nextStripes.forEach((stripe, i) => {
            const sCell = descriptor.stripes[i]?.cells[0];
            if (sCell) writeAnchor(sCell.position, { widthFraction: stripe.primarySize });
          });
        }
        return { stripes: nextStripes };
      });
    },
    [descriptor, writeAnchor],
  );

  const previewBottomEdge = useCallback(
    (newSize: number) => {
      const clamped = Math.max(MIN_HEIGHT_PX, Math.round(newSize));
      setSizing((prev) => {
        if (descriptor.orientation === 'row') {
          const lastIdx = prev.stripes.length - 1;
          const last = prev.stripes[lastIdx];
          if (!last) return prev;
          const nextStripes = prev.stripes.slice();
          nextStripes[lastIdx] = { ...last, primarySize: clamped };
          return { stripes: nextStripes };
        }
        const nextStripes = prev.stripes.map((stripe) => {
          const sizes = stripe.cellCrossSizes.slice();
          const before = sizes.slice(0, -1).reduce((a, b) => a + b, 0);
          const lastIdx = sizes.length - 1;
          sizes[lastIdx] = Math.max(MIN_HEIGHT_PX, clamped - before);
          return { ...stripe, cellCrossSizes: sizes };
        });
        return { stripes: nextStripes };
      });
    },
    [descriptor],
  );

  const commitBottomEdge = useCallback(
    (newSize: number) => {
      const clamped = Math.max(MIN_HEIGHT_PX, Math.round(newSize));
      if (descriptor.orientation === 'row') {
        const lastIdx = descriptor.stripes.length - 1;
        const lastStripe = descriptor.stripes[lastIdx];
        const firstCell = lastStripe?.cells[0];
        setSizing((prev) => {
          const last = prev.stripes[lastIdx];
          if (!last) return prev;
          const nextStripes = prev.stripes.slice();
          nextStripes[lastIdx] = { ...last, primarySize: clamped };
          return { stripes: nextStripes };
        });
        if (firstCell) writeAnchor(firstCell.position, { heightPx: clamped });
        return;
      }
      setSizing((prev) => {
        const nextStripes = prev.stripes.map((stripe, stripeIdx) => {
          const sizes = stripe.cellCrossSizes.slice();
          const before = sizes.slice(0, -1).reduce((a, b) => a + b, 0);
          const lastIdx = sizes.length - 1;
          const newLast = Math.max(MIN_HEIGHT_PX, clamped - before);
          sizes[lastIdx] = newLast;
          const lastCell = descriptor.stripes[stripeIdx]?.cells[lastIdx];
          if (lastCell) writeAnchor(lastCell.position, { heightPx: Math.round(newLast) });
          return { ...stripe, cellCrossSizes: sizes };
        });
        return { stripes: nextStripes };
      });
    },
    [descriptor, writeAnchor],
  );

  return {
    descriptor,
    sizing,
    previewCrossPair,
    commitCrossPair,
    previewCrossPairPx,
    commitCrossPairPx,
    previewStripePair,
    commitStripePair,
    previewBottomEdge,
    commitBottomEdge,
  };
};
